/**
 * Bulk Upload Trucks CSV schema — template generation, parsing, validation & posting.
 *
 * Truck-side parity with `bulkUploadSchema.ts`. Inserts into `truck_postings`.
 */

import { EQUIPMENT_FLAT_LIST } from "@/lib/equipmentTypes";
import { geocodeCity } from "@/lib/bulkUploadSchema";

export { geocodeCity };

/* ------------------------------------------------------------------ */
/*  Column definitions                                                 */
/* ------------------------------------------------------------------ */

export const BULK_TRUCK_CSV_HEADERS = [
  "available_from",
  "available_to",
  "origin_city",
  "origin_state",
  "destination_city",
  "destination_state",
  "equipment_type",
  "load_type",
  "length_ft",
  "weight_capacity_lbs",
  "rate_per_mile",
  "reference_id",
  "comments",
] as const;

const REQUIRED_FIELDS: ReadonlySet<string> = new Set([
  "available_from",
  "available_to",
  "origin_city",
  "origin_state",
  "destination_city",
  "destination_state",
  "equipment_type",
  "load_type",
  "length_ft",
  "weight_capacity_lbs",
]);

/* ------------------------------------------------------------------ */
/*  Load-type normalisation (FTL/LTL only for trucks)                  */
/* ------------------------------------------------------------------ */

const VALID_LOAD_TYPES = ["FTL", "LTL"] as const;

const LOAD_TYPE_ALIASES: Record<string, string> = {
  ftl: "FTL",
  full: "FTL",
  "full load": "FTL",
  "full truckload": "FTL",
  ltl: "LTL",
  partial: "LTL",
};

function normalizeLoadType(raw: string): string | null {
  const key = raw.trim().toLowerCase();
  return LOAD_TYPE_ALIASES[key] ?? null;
}

/* ------------------------------------------------------------------ */
/*  Equipment-type normalisation                                       */
/* ------------------------------------------------------------------ */

const equipLookup: Record<string, string> = {};
for (const item of EQUIPMENT_FLAT_LIST) {
  equipLookup[item.name.toLowerCase()] = item.enumValue;
  equipLookup[item.code.toLowerCase()] = item.enumValue;
  equipLookup[item.enumValue.toLowerCase()] = item.enumValue;
}

function normalizeEquipmentType(raw: string): string | null {
  const key = raw.trim().toLowerCase();
  return equipLookup[key] ?? null;
}

/* ------------------------------------------------------------------ */
/*  "Anywhere" helper                                                  */
/* ------------------------------------------------------------------ */

export function isAnywhereDestination(city: string): boolean {
  return city.trim().toLowerCase() === "anywhere";
}

/* ------------------------------------------------------------------ */
/*  Template generation                                                */
/* ------------------------------------------------------------------ */

const HINT_ROW = [
  "# YYYY-MM-DD",
  "YYYY-MM-DD",
  "City name",
  "2-letter state",
  'City name or "Anywhere"',
  "2-letter state (blank if Anywhere)",
  "Van/Flatbed/Reefer/StepDeck…",
  "FTL/LTL",
  "Positive number",
  "Positive number",
  "(optional)",
  "(optional)",
  "(optional)",
];

const EXAMPLE_ROW = [
  "2026-04-01",
  "2026-04-03",
  "Dallas",
  "TX",
  "Chicago",
  "IL",
  "Van",
  "FTL",
  "53",
  "45000",
  "2.45",
  "TRK123",
  "Driver prefers daytime pickups",
];

export function generateTruckTemplateCSV(): string {
  const header = BULK_TRUCK_CSV_HEADERS.join(",");
  const hints = HINT_ROW.join(",");
  const example = EXAMPLE_ROW.join(",");
  return `${header}\n${hints}\n${example}\n`;
}

export function downloadTruckTemplateCSV() {
  const csv = generateTruckTemplateCSV();
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "LaneRunner_Bulk_Truck_Template.csv";
  a.click();
  URL.revokeObjectURL(url);
}

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface BulkTruckRow {
  rowNumber: number;
  data: Record<string, string>;
  normalised?: {
    load_type: string;
    equipment_type: string;
    anywhere: boolean;
  };
}

export interface BulkTruckRowError {
  row: BulkTruckRow;
  errors: string[];
}

export interface BulkTruckParseResult {
  validRows: BulkTruckRow[];
  invalidRows: BulkTruckRowError[];
  totalRows: number;
}

/* ------------------------------------------------------------------ */
/*  CSV parsing                                                        */
/* ------------------------------------------------------------------ */

function parseLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
    } else if (ch === "," && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += ch;
    }
  }
  result.push(current.trim());
  return result;
}

/* ------------------------------------------------------------------ */
/*  Validation                                                         */
/* ------------------------------------------------------------------ */

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function validateTruckRow(row: BulkTruckRow): string[] {
  const errs: string[] = [];
  const d = row.data;
  const anywhere = isAnywhereDestination(d.destination_city ?? "");

  // Required fields presence (destination_state is not required when "Anywhere")
  for (const field of REQUIRED_FIELDS) {
    if (anywhere && field === "destination_state") continue;
    if (!d[field] || d[field].trim() === "") {
      errs.push(`Missing required field: ${field}`);
    }
  }
  if (errs.length > 0) return errs;

  // Date formats
  if (!DATE_RE.test(d.available_from)) {
    errs.push("available_from must be YYYY-MM-DD");
  }
  if (!DATE_RE.test(d.available_to)) {
    errs.push("available_to must be YYYY-MM-DD");
  }
  if (errs.length === 0 && d.available_to < d.available_from) {
    errs.push("available_to must be on or after available_from");
  }

  // Required numerics
  for (const field of ["length_ft", "weight_capacity_lbs"] as const) {
    const num = Number(d[field]);
    if (isNaN(num) || num <= 0) {
      errs.push(`${field} must be a positive number`);
    }
  }

  // Optional rate_per_mile
  if (d.rate_per_mile && d.rate_per_mile.trim() !== "") {
    const num = Number(d.rate_per_mile);
    if (isNaN(num) || num <= 0) {
      errs.push("rate_per_mile must be a positive number when provided");
    }
  }

  // Load type
  const normLoad = normalizeLoadType(d.load_type);
  if (!normLoad) {
    errs.push(`Invalid load_type "${d.load_type}" — use ${VALID_LOAD_TYPES.join(", ")}`);
  }

  // Equipment type
  const normEquip = normalizeEquipmentType(d.equipment_type);
  if (!normEquip) {
    errs.push(`Unrecognised equipment_type "${d.equipment_type}"`);
  }

  if (errs.length === 0 && normLoad && normEquip) {
    row.normalised = { load_type: normLoad, equipment_type: normEquip, anywhere };
  }

  return errs;
}

/* ------------------------------------------------------------------ */
/*  Payload builder                                                    */
/* ------------------------------------------------------------------ */

export interface BulkTruckPayload {
  company_id: string;
  created_by: string;
  available_from: string;
  available_to: string;
  origin_location: string;
  destination_location: string;
  equipment_type: string;
  load_type: string;
  length_ft: number;
  weight_capacity_lbs: number;
  rate_per_mile: number | null;
  reference_id: string | null;
  comments: string | null;
  status: string;
}

interface GeocodedCity {
  city: string;
  state: string;
  lat: number;
  lng: number;
}

export function buildTruckPayload(
  row: BulkTruckRow,
  origin: GeocodedCity,
  destination: GeocodedCity | null,
  companyId: string,
  userId: string,
): BulkTruckPayload {
  const d = row.data;
  const norm = row.normalised!;
  const rateRaw = d.rate_per_mile?.trim();
  return {
    company_id: companyId,
    created_by: userId,
    available_from: d.available_from,
    available_to: d.available_to,
    origin_location: `${origin.city}, ${origin.state}`,
    destination_location: norm.anywhere
      ? "Anywhere"
      : destination
      ? `${destination.city}, ${destination.state}`
      : `${d.destination_city}, ${d.destination_state}`,
    equipment_type: norm.equipment_type,
    load_type: norm.load_type,
    length_ft: Number(d.length_ft),
    weight_capacity_lbs: Number(d.weight_capacity_lbs),
    rate_per_mile: rateRaw ? Number(rateRaw) : null,
    reference_id: d.reference_id?.trim() || null,
    comments: d.comments?.trim() || null,
    status: "available",
  };
}

/* ------------------------------------------------------------------ */
/*  Public: parse + validate                                           */
/* ------------------------------------------------------------------ */

export function parseAndValidateTruckCSV(text: string): BulkTruckParseResult {
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
  if (lines.length === 0) return { validRows: [], invalidRows: [], totalRows: 0 };

  const headerLine = lines[0];
  const headers = parseLine(headerLine).map((h) => h.toLowerCase().replace(/\s+/g, "_"));

  const validRows: BulkTruckRow[] = [];
  const invalidRows: BulkTruckRowError[] = [];

  for (let i = 1; i < lines.length; i++) {
    if (lines[i].trimStart().startsWith("#")) continue;
    const values = parseLine(lines[i]);
    const data: Record<string, string> = {};
    headers.forEach((h, idx) => {
      data[h] = values[idx] ?? "";
    });

    const row: BulkTruckRow = { rowNumber: i + 1, data };
    const errors = validateTruckRow(row);

    if (errors.length > 0) {
      invalidRows.push({ row, errors });
    } else {
      validRows.push(row);
    }
  }

  return { validRows, invalidRows, totalRows: validRows.length + invalidRows.length };
}
